import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ImageIcon from "@mui/icons-material/Image";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { getAttachFileBlob } from "./SN_SYS_BAS_REP_001_api";

interface popupProps {
  modalRef?: any;
  selectedData?: any;
}

// sys_attachfile 기준 컬럼.
// 메뉴명/첨부자는 조인 결과 별칭이라 코드 컬럼(cd_menu, cd_user_reg)을 예비로 둔다.
const FIELD_CANDIDATES: { [key: string]: string[] } = {
  nmMenu: ["nm_menu", "cd_menu"],
  noDocu: ["no_docu"],
  dtDocu: ["dt_docu"],
  nmFile: ["nm_file"],
  nmFileExt: ["nm_file_ext"],
  nmUser: ["nm_user_reg", "nm_user", "cd_user_reg"],
  qtSize: ["qt_size"],
  nmDirectory: ["nm_directory"],
  id: ["id"], // 표시용이 아니라 썸네일 조회(/snotes/file/download)용
};

// 파일 유형: PDF / EXCEL / Word / PPT / 이미지 / 압축파일 / 기타
type FileType =
  | "PDF"
  | "EXCEL"
  | "WORD"
  | "PPT"
  | "IMAGE"
  | "ARCHIVE"
  | "ETC";

const EXTS_BY_TYPE: { type: FileType; exts: string[] }[] = [
  { type: "PDF", exts: ["pdf"] },
  { type: "EXCEL", exts: ["xls", "xlsx", "xlsm", "xlsb", "csv"] },
  { type: "WORD", exts: ["doc", "docx", "docm", "rtf"] },
  { type: "PPT", exts: ["ppt", "pptx", "pptm", "pps", "ppsx"] },
  {
    type: "IMAGE",
    exts: ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "tif", "tiff"],
  },
  { type: "ARCHIVE", exts: ["zip", "rar", "7z", "alz", "egg", "tar", "gz"] },
];

const pick = (row: any, key: string): any => {
  if (!row) return "";
  const candidates = FIELD_CANDIDATES[key] || [];
  for (const field of candidates) {
    const value = row[field];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

// 확장자는 nm_file_ext 컬럼을 우선 쓰고, 비어 있으면 파일명에서 잘라낸다.
const getExt = (extColumn: any, fileName: any): string => {
  const ext = String(extColumn || "").trim();
  if (ext !== "") return ext.replace(/^\./, "").toLowerCase();

  const name = String(fileName || "");
  const idx = name.lastIndexOf(".");
  return idx < 0 ? "" : name.slice(idx + 1).toLowerCase();
};

const getFileType = (ext: string): FileType => {
  const found = EXTS_BY_TYPE.find((item) => item.exts.includes(ext));
  return found ? found.type : "ETC";
};

// 파일 유형별 아이콘 / 색상
const getFileIcon = (fileType: FileType) => {
  const size = { fontSize: 56 };
  switch (fileType) {
    case "PDF":
      return <PictureAsPdfIcon sx={{ ...size, color: "#d32f2f" }} />;
    case "EXCEL":
      return <TableChartIcon sx={{ ...size, color: "#1d6f42" }} />;
    case "WORD":
      return <DescriptionIcon sx={{ ...size, color: "#2b579a" }} />;
    case "PPT":
      return <SlideshowIcon sx={{ ...size, color: "#d24726" }} />;
    case "IMAGE":
      return <ImageIcon sx={{ ...size, color: "#0288d1" }} />;
    case "ARCHIVE":
      return <FolderZipIcon sx={{ ...size, color: "#f9a825" }} />;
    default:
      return <InsertDriveFileIcon sx={{ ...size, color: "#757575" }} />;
  }
};

// 8자리 숫자(yyyyMMdd)면 yyyy-MM-dd 로 표시한다.
const formatDate = (value: any): string => {
  const text = String(value ?? "").trim();
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
  }
  return text;
};

// 숫자(byte)면 KB/MB 로 환산하고, 이미 문자열이면 그대로 쓴다.
const formatSize = (value: any): string => {
  const text = String(value ?? "").trim();
  if (text === "") return "";
  if (!/^\d+(\.\d+)?$/.test(text)) return text;

  const bytes = Number(text);
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)}GB`;
};

const SN_SYS_BAS_REP_001_POP_001 = forwardRef(
  ({ modalRef, selectedData }: popupProps, ref: React.Ref<any>) => {
    const [thumbnail, setThumbnail] = useState<string>("");

    useImperativeHandle(ref, () => ({
      getSelectedValue: async () => undefined,
    }));

    const row = useMemo(() => selectedData || {}, [selectedData]);

    const nmFile = String(pick(row, "nmFile") || "");
    const ext = getExt(pick(row, "nmFileExt"), nmFile);
    const fileType = getFileType(ext);
    const isImage = fileType === "IMAGE";
    const idFile = pick(row, "id");

    const infos = useMemo(
      () => [
        { label: "메뉴명", value: String(pick(row, "nmMenu") || "") },
        { label: "전표번호", value: String(pick(row, "noDocu") || "") },
        { label: "전표일자", value: formatDate(pick(row, "dtDocu")) },
        { label: "파일명", value: nmFile },
        { label: "첨부자", value: String(pick(row, "nmUser") || "") },
        { label: "크기", value: formatSize(pick(row, "qtSize")) },
        { label: "경로", value: String(pick(row, "nmDirectory") || "") },
      ],
      [row, nmFile]
    );

    // 이미지 파일이면 썸네일을 받아 보여주고, 실패하면 확장자 아이콘으로 대체한다.
    useEffect(() => {
      let objectUrl = "";
      let canceled = false;

      const loadThumbnail = async () => {
        if (!isImage || !idFile) return;

        try {
          const blob = await getAttachFileBlob(row);

          if (canceled) return;
          objectUrl = window.URL.createObjectURL(blob);
          setThumbnail(objectUrl);
        } catch (err) {
          // 썸네일 실패는 아이콘 표시로 대체하므로 별도 안내는 하지 않는다.
          console.error("썸네일 조회를 실패했습니다.", err);
        }
      };

      loadThumbnail();

      return () => {
        canceled = true;
        if (objectUrl) window.URL.revokeObjectURL(objectUrl);
      };
    }, [isImage, idFile, row]);

    return (
      <div className="flex flex-col gap-3">
        {/* 파일 아이콘(또는 이미지 썸네일) + 파일명 */}
        <div
          className="flex flex-col items-center justify-center gap-1"
          style={{
            border: "1px solid #babfc7",
            borderRadius: "4px",
            padding: "12px 8px",
            minHeight: "120px",
          }}
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={nmFile}
              style={{
                maxWidth: "100%",
                maxHeight: "120px",
                objectFit: "contain",
              }}
            />
          ) : (
            getFileIcon(fileType)
          )}
          <span
            style={{
              fontSize: "13px",
              wordBreak: "break-all",
              textAlign: "center",
            }}
          >
            {nmFile}
          </span>
        </div>

        {/* 문서 정보 */}
        <div className="flex flex-col gap-1">
          {infos.map((info) => (
            <div key={info.label} className="flex" style={{ fontSize: "13px" }}>
              <span
                style={{
                  width: "70px",
                  flexShrink: 0,
                  color: "#666",
                }}
              >
                {info.label}
              </span>
              <span style={{ wordBreak: "break-all" }}>
                : {info.value || "-"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default SN_SYS_BAS_REP_001_POP_001;
